import { useColorMode } from '@chakra-ui/react'
import * as Frigade from '@frigade/react'
import { PropsWithChildren, useMemo } from 'react'

import { colors } from '@common/theme/colors'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { useFirm } from '~/components/hooks/useFirm'
import { isDev, isProd } from '~/util/env'
import { avatarSidToUrl } from '~/util/s3'

function getFrigadeTheme(isDark: boolean) {
  return {
    colors: {
      primary: {
        foreground: colors.white,
        background: isDark ? colors.brand[400] : colors.brand[500],
        surface: isDark ? colors.brand[400] : colors.brand[500],
        border: isDark ? colors.brand[400] : colors.brand[500],
        hover: {
          background: isDark ? colors.brand[300] : colors.brand[600],
          surface: isDark ? colors.brand[300] : colors.brand[600],
          border: isDark ? colors.brand[300] : colors.brand[600],
        },
      },
      secondary: {
        foreground: isDark ? colors.gray[200] : colors.gray[800],
        background: isDark ? colors.gray[800] : colors.white,
        surface: isDark ? colors.gray[700] : colors.gray[100],
        border: isDark ? colors.gray[700] : colors.gray[200],
        hover: {
          background: isDark ? colors.gray[700] : colors.gray[50],
          surface: isDark ? colors.gray[600] : colors.gray[200],
          border: isDark ? colors.gray[600] : colors.gray[300],
        },
      },
      neutral: {
        foreground: isDark ? colors.gray[200] : colors.gray[800],
        background: isDark ? colors.gray[800] : colors.white,
        surface: isDark ? colors.gray[700] : colors.gray[100],
        border: isDark ? colors.gray[700] : colors.gray[200],
        '100': isDark ? colors.gray[50] : colors.gray[900],
        '200': isDark ? colors.gray[100] : colors.gray[800],
        '300': isDark ? colors.gray[200] : colors.gray[700],
        '400': isDark ? colors.gray[300] : colors.gray[600],
        '500': isDark ? colors.gray[400] : colors.gray[500],
        '600': isDark ? colors.gray[500] : colors.gray[400],
        '700': isDark ? colors.gray[600] : colors.gray[300],
        '800': isDark ? colors.gray[700] : colors.gray[200],
        '900': isDark ? colors.gray[800] : colors.gray[50],
      },
    },
    fontFamilies: {
      default: 'Inter Variable, -apple-system, system-ui, sans-serif',
    },
    radii: {
      md: '6px',
      lg: '12px',
    },
  }
}

export function FrigadeEngageWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const firm = useFirm()
  const { isImpersonating } = useAdminContext()
  const isSystemAdmin = user.admin
  const { colorMode } = useColorMode()

  const frigadeApiKey = process.env.NEXT_PUBLIC_FRIGADE_ENGAGE_API_KEY

  const shouldUseFrigadeEngage =
    frigadeApiKey && (isDev || (isProd && !isImpersonating && !isSystemAdmin))

  const frigadeTheme = useMemo(() => getFrigadeTheme(colorMode === 'dark'), [colorMode])

  if (!shouldUseFrigadeEngage) {
    return <>{children}</>
  }

  const [firstName, ...lastNameParts] = user.fullName.split(' ')
  const lastName = lastNameParts.join(' ')
  const imageUrl = user.avatarSid ? avatarSidToUrl(user.avatarSid) : undefined
  const userProperties = {
    name: user.fullName,
    firstName: firstName ?? user.fullName,
    lastName: lastName ?? '',
    email: user.email,
    imageUrl,
    role: user.role,
    timeCreated: user.timeCreated,
  }

  const groupProperties = {
    name: firm.name,
    timeCreated: firm.timeCreated,
    isDemo: firm.isDemo,
  }

  return (
    <Frigade.Provider
      apiKey={frigadeApiKey}
      userId={user.sid}
      userProperties={userProperties}
      groupId={firm.sid}
      groupProperties={groupProperties}
      theme={frigadeTheme}
    >
      {children}
    </Frigade.Provider>
  )
}
